import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const { t } = useLang();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("demoivre_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [adminToken, setAdminTokenState] = useState(
    () => localStorage.getItem("demoivre_admin") || ""
  );
  const [settings, setSettings] = useState({});

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  useEffect(() => {
    localStorage.setItem("demoivre_cart", JSON.stringify(cart));
  }, [cart]);

  const refreshArtworks = useCallback(async () => {
    try {
      const { data } = await api.get("/artworks");
      setArtworks(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshArtworks();
  }, [refreshArtworks]);

  const addToCart = (art) => {
    if (cart.some((i) => i.artwork_id === art.artwork_id)) {
      toast.info(t("store.alreadyIn"));
      setCartOpen(true);
      return;
    }
    setCart((c) => [
      ...c,
      {
        artwork_id: art.artwork_id,
        title: art.title,
        title_en: art.title_en || "",
        dwg_no: art.dwg_no,
        dimensions: art.dimensions,
        price: art.price,
        image_url: art.image_url,
      },
    ]);
    toast.success(t("store.added"));
    setCartOpen(true);
  };

  const removeFromCart = (id) => setCart((c) => c.filter((i) => i.artwork_id !== id));
  const clearCart = () => setCart([]);

  const setAdminToken = (t) => {
    setAdminTokenState(t);
    if (t) localStorage.setItem("demoivre_admin", t);
    else localStorage.removeItem("demoivre_admin");
  };

  return (
    <StoreContext.Provider
      value={{
        artworks,
        loading,
        refreshArtworks,
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        cartOpen,
        setCartOpen,
        checkoutOpen,
        setCheckoutOpen,
        adminToken,
        setAdminToken,
        settings,
        refreshSettings,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
